import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import Container from '@/components/generals/container'
import { Eyebrow, displayTitleClass } from '@/components/generals/section-heading'
import { quoteHref } from '@/constants/site'

interface CtaBannerProps {
    id?: string
    eyebrow?: string
    title?: ReactNode
    description?: ReactNode
    buttonLabel?: string
    href?: string
    secondary?: {
        label: string
        href: string
    }
}

const CtaBanner: FC<CtaBannerProps> = ({
    id = 'cotizar',
    eyebrow = 'Cotización',
    title = 'Tu próximo reconocimiento comienza aquí.',
    description = 'Cuéntanos qué deseas celebrar y exploremos juntos las posibilidades.',
    buttonLabel = 'Solicitar cotización',
    href = quoteHref,
    secondary
}): ReactNode => {
    return (
        <section
            id={id}
            aria-labelledby={`${id}-titulo`}
            className='surface-brushed relative overflow-hidden border-t border-ivory/10 bg-black py-24 md:py-32'
        >
            <Container size='narrow' className='reveal relative flex flex-col items-center gap-7 text-center'>
                <Eyebrow centered>{eyebrow}</Eyebrow>

                <h2 id={`${id}-titulo`} className={cn(displayTitleClass, 'max-w-3xl text-4xl sm:text-5xl lg:text-6xl')}>
                    {title}
                </h2>

                <p className='max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg'>
                    {description}
                </p>

                <div className='mt-2 flex flex-col items-center gap-4 sm:flex-row'>
                    <Link href={href} className={buttonVariants({ size: 'lg' })}>
                        {buttonLabel}
                        <ArrowRight aria-hidden='true' />
                    </Link>

                    {secondary && (
                        <Link href={secondary.href} className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                            {secondary.label}
                        </Link>
                    )}
                </div>
            </Container>
        </section>
    )
}

export default CtaBanner
